import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Trash2, Loader2, ArrowLeft, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { fetchInventoryOptimized, clearInventoryCache } from "@/lib/api/inventory";
import { saveDocument, generateDocumentNumber } from "@/lib/api/documents";
import { AVAILABLE_SIZES } from "@/lib/constants";
import { ProductSelector } from "@/components/ProductSelector";
import { CustomerSelector } from "@/components/CustomerSelector";
import { ColorBadge } from "@/components/ColorBadge";
import { Table, TableHeader, TableBody, TableCell, TableHead, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export function DocumentForm({ type, documentId = null, onClose }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(true);
  const [document, setDocument] = useState({
    number: "",
    date: new Date().toISOString().split('T')[0],
    customerId: "",
    items: [],
    is_regularization: false,
    regularization_reason: ""
  });
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantitiesBySize, setQuantitiesBySize] = useState({});
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [allSizes, setAllSizes] = useState([]);
  const { toast } = useToast();

  // Calcular totales del documento
  const documentTotals = document.items.reduce((acc, item) => {
    acc.totalQuantity += item.quantity;
    acc.totalAmount += item.quantity * item.price;
    return acc;
  }, { totalQuantity: 0, totalAmount: 0 });

  useEffect(() => {
    const checkRole = async () => {
      setIsLoadingRole(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data: userData, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', session.user.id)
          .single();

        if (error) throw error;
        const isAdminUser = userData.role === 'admin';
        setIsAdmin(isAdminUser);
        
        if (type === 'salida' && !isAdminUser) {
          toast({
            title: "Acceso denegado",
            description: "Solo los administradores pueden crear documentos de salida",
            variant: "destructive"
          });
          onClose();
        }
      } catch (error) {
        console.error('Error checking user role:', error);
        toast({
          title: "Error",
          description: "No se pudo verificar el rol del usuario",
          variant: "destructive"
        });
      } finally {
        setIsLoadingRole(false);
      }
    };

    checkRole();
  }, [type]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (documentId) {
      fetchDocument();
    } else {
      generateNewDocumentNumber();
    }
  }, [documentId, type]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [productsRes, customersRes] = await Promise.all([
        supabase.from('products').select('*').eq('active', true),
        supabase.from('customers').select('*')
      ]);
      const inventoryData = await fetchInventoryOptimized();

      if (productsRes.error) throw productsRes.error;
      if (customersRes.error) throw customersRes.error;

      const productsData = productsRes.data || [];
      setProducts(productsData);
      setCustomers(customersRes.data || []);
      setInventory(inventoryData || []);

      // Definir el orden específico de las tallas
      const sizeOrder = ["P", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];
      
      // Extraer todas las tallas únicas de los productos y ordenarlas según el orden específico
      const uniqueSizes = [...new Set(productsData.flatMap(p => p.sizes))]
        .sort((a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b));
      setAllSizes(uniqueSizes);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos iniciales",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const generateNewDocumentNumber = async () => {
    try {
      const newNumber = await generateDocumentNumber(type);
      setDocument(prev => ({ ...prev, number: newNumber }));
    } catch (error) {
      console.error('Error generating document number:', error);
      toast({
        title: "Error",
        description: "No se pudo generar el número de documento",
        variant: "destructive"
      });
    }
  };

  const fetchDocument = async () => {
    setIsLoadingDocument(true);
    try {
      const { data, error } = await supabase
        .from('documents')
        .select(`
          *,
          items:document_items(*, product:products(*))
        `)
        .eq('id', documentId)
        .single();

      if (error) throw error;
      if (data) {
        const formattedDate = data.date ? new Date(data.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
        
        setDocument({
          id: data.id,
          number: data.number,
          date: formattedDate,
          customerId: data.customer_id || "",
          is_regularization: !!data.is_regularization,
          regularization_reason: data.regularization_reason || "",
          items: data.items?.map(item => ({
            ...item,
            product_code: item.product?.code,
            description: item.product?.description
          })) || []
        });
      }
    } catch (error) {
      console.error('Error fetching document:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el documento",
        variant: "destructive"
      });
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const calculateCurrentStock = (productId, size, color) => {
    return inventory
      .filter(item => 
        item.product_id === productId && 
        item.size === size && 
        item.color === color
      )
      .reduce((total, item) => total + item.quantity, 0);
  };

  const handleAddItem = async () => {
    const product = products.find(p => p.id === selectedProduct);
    if (!product || !selectedColor) return;

    const colorQuantities = quantitiesBySize[selectedColor] || {};
    const hasQuantities = type === 'entrada' && document.is_regularization
      ? Object.values(colorQuantities).some(qty => (parseInt(qty) || 0) !== 0)
      : Object.values(colorQuantities).some(qty => (parseInt(qty) || 0) > 0);

    if (!hasQuantities) return;

    const newItems = product.sizes.map(size => {
      const raw = colorQuantities[size];
      const quantity = (typeof raw === 'string' && (raw === '' || raw === '-')) ? 0 : (parseInt(raw) || 0);
      // Reglas: en salida solo positivos; en entrada normal solo positivos; en regularización (entrada) permitir negativos y positivos, pero no 0
      if (type === 'salida' && quantity <= 0) return null;
      if (type === 'entrada' && !document.is_regularization && quantity <= 0) return null;
      if (type === 'entrada' && document.is_regularization && quantity === 0) return null;

      if (type === "salida") {
        const currentStock = calculateCurrentStock(selectedProduct, size, selectedColor);
        if (quantity > currentStock) {
          toast({
            title: "Advertencia",
            description: `Stock insuficiente para ${product.code} - Talla: ${size}, Color: ${selectedColor}. Stock disponible: ${currentStock}`,
            variant: "destructive"
          });
          return null;
        }
      }

      // Validación adicional: en regularización con cantidad negativa, no dejar stock final negativo
      if (type === 'entrada' && document.is_regularization && quantity < 0) {
        const currentStock = calculateCurrentStock(selectedProduct, size, selectedColor);
        if (Math.abs(quantity) > currentStock) {
          toast({
            title: "Advertencia",
            description: `Stock insuficiente para ajustar ${product.code} - Talla: ${size}, Color: ${selectedColor}. Disponible: ${currentStock}, ajuste: ${quantity}`,
            variant: "destructive"
          });
          return null;
        }
      }

      return {
        product_id: selectedProduct,
        product_code: product.code,
        description: product.description,
        size,
        color: selectedColor,
        quantity,
        price: product.price
      };
    }).filter(Boolean);

    if (newItems.length > 0) {
      setDocument(prev => ({
        ...prev,
        items: [...prev.items, ...newItems]
      }));

      // Mantener el producto seleccionado, limpiar solo el color
      setSelectedColor("");
      // No limpiar selectedProduct ni quantitiesBySize
    }
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    // No limpiar las cantidades al cambiar de color
  };

  const handleQuantityChange = (color, size, value) => {
    // En regularización permitimos valores intermedios como '-' o '' sin convertir
    const isReg = type === 'entrada' && document.is_regularization;
    let nextValue;
    if (isReg) {
      nextValue = value; // mantener string tal cual durante la edición
    } else {
      const parsed = parseInt(value);
      nextValue = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    }

    setQuantitiesBySize(prev => ({
      ...prev,
      [color]: {
        ...prev[color],
        [size]: nextValue
      }
    }));
  };

  const handleEditQuantity = (productId, color, size, value) => {
    const isReg = type === 'entrada' && document.is_regularization;
    const parsed = parseInt(value);
    const nextQuantity = isReg ? (Number.isFinite(parsed) ? parsed : 0) : (Number.isFinite(parsed) ? Math.max(0, parsed) : 0);
    setDocument(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.product_id === productId && item.color === color && item.size === size
          ? { ...item, quantity: nextQuantity }
          : item
      )
    }));
  };

  const handleRemoveItem = (index) => {
    setDocument(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const clearQuantities = () => {
    setQuantitiesBySize({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving) return;

    if (type === "salida" && !document.customerId) {
      toast({
        title: "Error",
        description: "Debe seleccionar un cliente",
        variant: "destructive"
      });
      return;
    }

    if (document.items.length === 0) {
      toast({
        title: "Error",
        description: "Debe agregar al menos un ítem",
        variant: "destructive"
      });
      return;
    }

    if (type === 'salida' && !isAdmin) {
      toast({
        title: "Acceso denegado",
        description: "Solo los administradores pueden crear documentos de salida",
        variant: "destructive"
      });
      return;
    }

    try {
      // Validación previa: en regularización, ninguna línea puede dejar stock final en negativo
      if (type === 'entrada' && document.is_regularization) {
        for (const item of document.items) {
          if (item.quantity < 0) {
            const currentStock = calculateCurrentStock(item.product_id, item.size, item.color);
            if (Math.abs(item.quantity) > currentStock) {
              toast({
                title: "Error",
                description: `Ajuste inválido para ${item.product_code} (${item.size}/${item.color}). Disponible: ${currentStock}, ajuste: ${item.quantity}`,
                variant: "destructive"
              });
              return;
            }
          }
        }
      }
      setSaving(true);
      const result = await saveDocument(document, type);
      if (!result) return;

      try {
        // Invalidar caché de inventario para que el siguiente fetch refleje los cambios
        clearInventoryCache();
      } catch (_) {}

      toast({
        title: "Éxito",
        description: `Documento de ${type} guardado exitosamente`,
      });

      window.location.hash = `#/${type}s`;
      onClose();
    } catch (error) {
      console.error('Error saving document:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar el documento",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoadingRole) {
    return <div>Cargando...</div>;
  }
  if (loading) {
    return <div>Cargando...</div>;
  }
  if (isLoadingDocument) {
    return <div className="p-6 text-sm">Cargando documento...</div>;
  }

  const selectedProductData = products.find(p => p.id === selectedProduct);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.location.hash = `#/${type}s`}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
            <div>
              <h2 className="text-2xl font-bold mb-2">
                {type === 'entrada' ? 'Entrada de Productos' : 'Salida de Productos'}
              </h2>
              <div className="flex gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Total Unidades:</span> {documentTotals.totalQuantity}
                </div>
                <div>
                  <span className="font-medium">Total Importe:</span> {documentTotals.totalAmount.toFixed(2)} €
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="number">Número</Label>
            <Input
              id="number"
              required
              value={document.number}
              onChange={(e) => setDocument(prev => ({ ...prev, number: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="date">Fecha</Label>
            <Input
              id="date"
              type="date"
              required
              value={document.date || new Date().toISOString().split('T')[0]}
              onChange={(e) => setDocument(prev => ({ ...prev, date: e.target.value || new Date().toISOString().split('T')[0] }))}
            />
          </div>
          {type === "salida" && (
            <div className="col-span-2">
              <Label htmlFor="customerId">Cliente</Label>
              <CustomerSelector
                id="customerId"
                value={document.customerId}
                onSelect={(customerId) => setDocument(prev => ({ ...prev, customerId: customerId }))}
              />
            </div>
          )}
          {type === 'entrada' && (
            <div className="col-span-2 grid grid-cols-2 gap-4 items-end">
              <div className="flex items-center gap-2">
                <input
                  id="is_regularization"
                  type="checkbox"
                  checked={document.is_regularization}
                  onChange={(e) => {
                    if (!isAdmin && e.target.checked) {
                      toast({ title: 'Acceso denegado', description: 'Solo administradores pueden activar regularización', variant: 'destructive' });
                      return;
                    }
                    setDocument(prev => ({ ...prev, is_regularization: e.target.checked }));
                  }}
                  className="h-4 w-4"
                />
                <Label htmlFor="is_regularization">Regularización (permitir cantidades negativas)</Label>
              </div>
              {document.is_regularization && (
                <div className="col-span-2">
                  <Label htmlFor="regularization_reason">Motivo (opcional)</Label>
                  <textarea
                    id="regularization_reason"
                    value={document.regularization_reason}
                    onChange={(e) => setDocument(prev => ({ ...prev, regularization_reason: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm"
                    rows={2}
                    placeholder="Describe el motivo del ajuste"
                  />
                </div>
              )}
            </div>
          )}
          {type === 'entrada' && document.is_regularization && (
            <div className="col-span-2">
              <div className="mt-2 p-3 rounded-md border border-yellow-300 bg-yellow-50 text-yellow-800 text-sm">
                Modo regularización activo: se permiten cantidades negativas. Los ajustes negativos no pueden dejar el stock por debajo de 0.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium mb-4">Agregar Productos</h3>
        <div className="space-y-4">
          <div>
            <Label htmlFor="product">Producto</Label>
            <ProductSelector
              value={selectedProduct}
              onSelect={(productId) => {
                setSelectedProduct(productId);
                setSelectedColor("");
              }}
            />
          </div>

          {selectedProductData && (
            <>
              <div>
                <Label htmlFor="colors">Color</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedProductData.colors.map((color) => (
                    <Button
                      key={color}
                      type="button"
                      variant={selectedColor === color ? "default" : "outline"}
                      onClick={() => handleColorSelect(color)}
                      className="flex items-center gap-2"
                    >
                      <ColorBadge color={color} />
                    </Button>
                  ))}
                </div>
              </div>

              {selectedColor && (
                <div className="mt-4">
                  <Label>Cantidades por talla</Label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <ColorBadge color={selectedColor} />
                        <span className="font-medium">{selectedColor}</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearQuantities}
                        className="flex items-center gap-1"
                      >
                        <X className="h-3 w-3" />
                        Limpiar cantidades
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedProductData.sizes
                        .sort((a, b) => {
                          const sizeOrder = ["P", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];
                          return sizeOrder.indexOf(a) - sizeOrder.indexOf(b);
                        })
                        .map(size => (
                        <div key={size} className="flex flex-col">
                          <Label htmlFor={`quantity-${selectedColor}-${size}`} className="text-xs mb-1">{size}</Label>
                          <Input
                            id={`quantity-${selectedColor}-${size}`}
                            type="number"
                        min={type === 'entrada' && document.is_regularization ? -999 : 0}
                            max="999"
                            value={(quantitiesBySize[selectedColor]?.[size] ?? 0)}
                            onChange={(e) => handleQuantityChange(selectedColor, size, e.target.value)}
                            className="w-16 h-8 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end mt-4">
                <Button
                  type="button"
                  onClick={handleAddItem}
                  disabled={!selectedProduct || !selectedColor}
                >
                  <Plus className="h-4 w-4 mr-2" /> Agregar productos
                </Button>
              </div>

              {(type === "salida" || (type === 'entrada' && document.is_regularization)) && selectedColor && (
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <p className="text-sm font-medium mb-2">Stock disponible por combinación:</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {selectedProductData.sizes.map(size => {
                      const stock = calculateCurrentStock(selectedProduct, size, selectedColor);
                      return (
                        <div key={`${size}-${selectedColor}`} className="text-sm">
                          {size}: <span className={stock === 0 ? 'text-red-500 font-bold' : 'font-medium'}>{stock}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Producto</TableHead>
                <TableHead className="w-[80px]">Imagen</TableHead>
                <TableHead className="w-[80px]">Color</TableHead>
                {allSizes.map(size => (
                  <TableHead key={size} className="text-center w-[70px]">{size}</TableHead>
                ))}
                <TableHead className="text-center w-[80px]">Total Unid.</TableHead>
                <TableHead className="text-center w-[80px]">Precio</TableHead>
                <TableHead className="text-center w-[100px]">Total</TableHead>
                <TableHead className="text-right w-[60px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(
                document.items.reduce((acc, item) => {
                  const key = `${item.product_id}-${item.color}`;
                  if (!acc[key]) {
                    const product = products.find(p => p.id === item.product_id);
                    acc[key] = {
                      product_id: item.product_id,
                      product_code: item.product_code,
                      description: item.description,
                      color: item.color,
                      sizes: {},
                      total: 0,
                      total_quantity: 0,
                      price: item.price,
                      product: product,
                      available_sizes: product?.sizes || []
                    };
                  }
                  acc[key].sizes[item.size] = item.quantity;
                  acc[key].total_quantity += item.quantity;
                  acc[key].total += item.quantity * item.price;
                  return acc;
                }, {})
              ).map(([key, item]) => (
                <TableRow key={key}>
                  <TableCell className="max-w-[250px] truncate">
                    {item.product_code} - {item.description}
                  </TableCell>
                  <TableCell>
                    {item.product?.images?.length > 0 && (
                      <div className="w-16 h-16 flex-shrink-0">
                        <img
                          src={item.product.images[item.product.main_image_index || 0]}
                          alt={item.description}
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <ColorBadge color={item.color} />
                  </TableCell>
                  {allSizes.map(size => (
                    <TableCell key={size} className="text-center p-1">
                      {item.available_sizes.includes(size) ? (
                        <Input
                          type="number"
                          min={type === 'entrada' && document.is_regularization ? -999 : 0}
                          max="999"
                          value={document.items.find(i => 
                            i.product_id === item.product_id && 
                            i.color === item.color && 
                            i.size === size
                          )?.quantity || 0}
                          onChange={(e) => handleEditQuantity(item.product_id, item.color, size, e.target.value)}
                          className="w-16 h-8 text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </TableCell>
                  ))}
                  <TableCell className="text-center whitespace-nowrap">
                    <span className="text-sm font-medium">{item.total_quantity}</span>
                  </TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    <span className="text-sm text-gray-600">{item.price.toFixed(2)} €</span>
                  </TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    <span className="font-medium">{item.total.toFixed(2)} €</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => {
                        setDocument(prev => ({
                          ...prev,
                          items: prev.items.filter(i => 
                            !(i.product_id === item.product_id && i.color === item.color)
                          )
                        }));
                      }}
                      disabled={saving}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.location.hash = `#/${type}s`}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : (
            'Guardar Documento'
          )}
        </Button>
      </div>
    </form>
  );
}
